import { __ } from '@wordpress/i18n';
import React from 'react';
import { createRoot } from 'react-dom/client';
import ReactPlayer from 'react-player';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';

const WISTIA_URL_PATTERN =
	/(?:wistia\.(?:com|net)|wi\.st)\/(?:medias|embed|s)\/([a-z0-9]+)/i;
const WISTIA_PLAYER_SCRIPT_URL = 'https://fast.wistia.com/player.js';
let wistiaScriptPromise;

const getWistiaMediaId = (url) => {
	const match = url?.match(WISTIA_URL_PATTERN);

	return match ? match[1] : null;
};

// Wistia URLs need the modern Wistia Player custom element instead of
// react-player's built-in Wistia support, which only speaks the deprecated
// E-v1.js embed API and doesn't render current Wistia embeds.
const loadWistiaPlayerScript = () => {
	if (!wistiaScriptPromise) {
		wistiaScriptPromise = new Promise((resolve, reject) => {
			if (
				document.querySelector(
					`script[src="${WISTIA_PLAYER_SCRIPT_URL}"]`
				)
			) {
				resolve();
				return;
			}

			const script = document.createElement('script');
			script.src = WISTIA_PLAYER_SCRIPT_URL;
			script.async = true;
			script.onload = resolve;
			script.onerror = reject;
			document.body.appendChild(script);
		});
	}

	return wistiaScriptPromise;
};

const videoComponent = () => {
	const videoComponents = document.querySelectorAll(
		'.component-video:not(.component-lightbox__button):not([data-lightbox-content])'
	);
	let videoLightbox;
	let videoLightboxContent;
	let root;
	const inlineRoots = new WeakMap();

	const initializeVideoLightbox = (videoLightboxComponent) => {
		const videoLightboxFigure = videoLightboxComponent;
		const isInline = videoLightboxFigure.classList.contains(
			'component-video--inline'
		);
		const clickHandler = isInline ? playInlineVideo : openVideoLightbox;

		videoLightboxFigure.classList.add('component-video__button');
		// open video - play icon click
		videoLightboxFigure
			?.querySelector('.component-video__play-button')
			?.addEventListener('click', clickHandler);
		videoLightboxFigure.addEventListener('click', clickHandler);
		videoLightboxFigure.setAttribute(
			'aria-label',
			isInline
				? __('Play Video', 'propel')
				: __('Open Image in Lightbox', 'propel')
		);
	};

	// play video inline, in place of the poster image
	const playInlineVideo = (e) => {
		e.preventDefault();

		const inlineVideoComponent = e.currentTarget.closest(
			'.component-video--inline'
		);

		if (!inlineVideoComponent) {
			return;
		}

		let inlineVideoWrapper = inlineVideoComponent.querySelector(
			'.component-video__inline-video-wrapper'
		);

		if (!inlineVideoWrapper) {
			inlineVideoWrapper = document.createElement('div');
			inlineVideoWrapper.classList.add(
				'component-video__inline-video-wrapper'
			);
			inlineVideoComponent.appendChild(inlineVideoWrapper);
		}

		playVideo(inlineVideoComponent, inlineVideoWrapper, true);
	};

	// open lightbox
	const openVideoLightbox = (e) => {
		e.preventDefault();

		if (!videoLightbox) {
			createVideoLightbox();
		}

		const lightboxVideoComponent =
			e.currentTarget.closest('.component-video');
		const lightboxVideoContent = videoLightbox.querySelector(
			'.component-video-lightbox__content'
		);

		videoLightbox.classList.add('active');

		disableBodyScroll(videoLightbox, {
			reserveScrollBarGap: true,
		});

		setTimeout(function () {
			videoLightbox.classList.add('visible');
		}, 1);

		document.addEventListener('keydown', keydown);

		playVideo(lightboxVideoComponent, lightboxVideoContent);
	};

	const playVideo = (
		lightboxVideoComponent,
		lightboxVideoContent,
		isInline = false
	) => {
		if (
			!lightboxVideoComponent ||
			!lightboxVideoComponent.hasAttribute('data-embed-url')
		) {
			return;
		}

		if (
			lightboxVideoComponent.classList.contains(
				'component-video-lightbox__video--playing'
			)
		) {
			return;
		}

		if (!isInline) {
			stopVideos();
		}

		const videoURL = lightboxVideoComponent.getAttribute('data-embed-url');
		const aspectRatio =
			lightboxVideoComponent.getAttribute('data-aspect-ratio') ??
			'1.777777778';
		const wistiaMediaId = getWistiaMediaId(videoURL);
		lightboxVideoComponent.classList.add(
			'component-video-lightbox__video--playing'
		);

		if (isInline) {
			lightboxVideoComponent.style.setProperty(
				'--aspectRatio',
				aspectRatio
			);
		}

		const videoRoot = createRoot(lightboxVideoContent);

		if (isInline) {
			inlineRoots.set(lightboxVideoComponent, videoRoot);
		} else {
			root = videoRoot;
		}

		// Poster stays visible until the embed reports it's actually
		// playing, masking each provider's own thumbnail/loading state
		// instead of flashing it in before playback starts.
		const markInlineVideoReady = () => {
			lightboxVideoComponent.classList.add(
				'component-video-lightbox__video--ready'
			);
		};

		if (wistiaMediaId) {
			loadWistiaPlayerScript().then(() => {
				videoRoot.render(
					<wistia-player
						className="component-video-lightbox__video"
						media-id={wistiaMediaId}
						autoplay
						style={{ '--aspectRatio': aspectRatio }}
						ref={
							isInline
								? (node) =>
										node?.addEventListener(
											'play',
											markInlineVideoReady,
											{ once: true }
										)
								: undefined
						}
					></wistia-player>
				);
			});

			return;
		}

		videoRoot.render(
			<ReactPlayer
				className="component-video-lightbox__video"
				url={videoURL}
				playing
				playsinline={true}
				height="100%"
				width="100%"
				controls={true}
				style={{ '--aspectRatio': aspectRatio }}
				onPlay={isInline ? markInlineVideoReady : undefined}
			/>
		);
	};

	const playVideoEvent = (e) => {
		if (
			!e?.detail?.lightboxVideoComponent ||
			!e?.detail?.lightboxVideoContent
		) {
			return;
		}

		const lightboxVideoComponent = e.detail.lightboxVideoComponent;
		const lightboxVideoContent = e.detail.lightboxVideoContent;

		playVideo(lightboxVideoComponent, lightboxVideoContent);
	};

	const stopVideos = () => {
		if (root && root._internalRoot) {
			root.unmount();
		}

		const playingVideos = document.querySelectorAll(
			'.component-video-lightbox__video--playing:not(.component-video--inline)'
		);

		playingVideos.forEach((playingVideo) => {
			playingVideo.classList.remove(
				'component-video-lightbox__video--playing'
			);
		});
	};

	const closeVideoLightbox = (e) => {
		e.preventDefault();

		videoLightbox.classList.remove('visible');

		stopVideos();

		setTimeout(function () {
			videoLightbox.classList.remove('active');
			clearAllBodyScrollLocks();
			videoLightboxContent.innerHTML = '';
		}, 400);

		root.unmount();

		document.removeEventListener('keydown', closeVideoLightbox);
	};

	const keydown = (e) => {
		if ('Escape' === e.key) {
			closeVideoLightbox(e);
		}
	};

	// create lightbox
	const createVideoLightbox = () => {
		videoLightbox = document.createElement('div');
		videoLightbox.classList.add('bg-dark', 'component-video-lightbox');
		videoLightbox.setAttribute('role', 'dialog');
		videoLightbox.setAttribute('aria-modal', 'true');

		const videoLightboxContainer = document.createElement('div');
		videoLightboxContainer.classList.add(
			'component-video-lightbox__container'
		);

		videoLightboxContent = document.createElement('div');
		videoLightboxContent.classList.add('component-video-lightbox__content');

		const videoLightboxCloseOverlay = document.createElement('button');
		videoLightboxCloseOverlay.classList.add(
			'component-video-lightbox__close-button-overlay'
		);
		videoLightboxCloseOverlay.setAttribute('role', 'button');
		videoLightboxCloseOverlay.setAttribute(
			'aria-label',
			__('Close Lightbox', 'propel')
		);
		videoLightboxCloseOverlay.addEventListener('click', closeVideoLightbox);

		const videoLightboxCloseButton = document.createElement('span');
		videoLightboxCloseButton.classList.add(
			'component-video-lightbox__close-button'
		);
		videoLightboxCloseButton.addEventListener('click', closeVideoLightbox);

		videoLightboxCloseOverlay.appendChild(videoLightboxCloseButton);
		videoLightboxContainer.appendChild(videoLightboxContent);
		videoLightbox.appendChild(videoLightboxContainer);
		videoLightbox.appendChild(videoLightboxCloseOverlay);
		document.body.appendChild(videoLightbox);
	};

	videoComponents.forEach((video) => {
		initializeVideoLightbox(video);
	});

	window.addEventListener('propel-play-video', playVideoEvent);
	window.addEventListener('propel-stop-videos', stopVideos);
};

videoComponent();

export default videoComponent;
